/**
 * Shared employee document utility functions.
 * Extracted from EmployeeDetail.js for reuse in TicketDetailView.js and other contexts.
 */

/**
 * Map frontend camelCase or backend snake_case document type to processing enum (UPPER_SNAKE).
 * Used to determine the OCR/extraction processing type for a given document category.
 */
export const toProcessingDocumentType = (t) => {
  const raw = String(t || '').trim();
  const lowerNoSpace = raw.toLowerCase().replace(/\s+/g, '');
  // Robust normalization for common categories
  if (lowerNoSpace === 'passportcopy' || lowerNoSpace === 'passport') return 'PASSPORT_COPY';
  if (lowerNoSpace === 'visapage' || raw === 'VISA_PAGE' || raw === 'visaPage') return 'VISA_PAGE';
  if (lowerNoSpace === 'ohc') return 'OHC';
  if (lowerNoSpace === 'drivinglicense' || raw === 'drivingLicense') return 'DRIVING_LICENSE';
  if (lowerNoSpace === 'drivinglicence' || raw === 'driving_licence') return 'DRIVING_LICENSE';
  switch (raw) {
    case 'passportCopy': return 'PASSPORT_COPY';
    case 'visaPage':
    case 'VISA_PAGE': return 'VISA_PAGE';
    case 'salaryCertificate': return 'SALARY_CERTIFICATE';
    case 'birthCertificate': return 'BIRTH_CERTIFICATE';
    case 'emiratesId': return 'EMIRATES_ID';
    case 'photo': return 'PHOTO';
    case 'ohc': return 'OHC';
    case 'drivingLicense': return 'DRIVING_LICENSE';
    default:
      return raw
        .replace(/([a-z])([A-Z])/g, '$1_$2')
        .replace(/\s+/g, '_')
        .toUpperCase();
  }
};

/**
 * Check if a processing document type supports OCR/extraction.
 */
export const isExtractionSupported = (ptype) =>
  ['PASSPORT_COPY', 'EMIRATES_ID', 'VISA_PAGE', 'OHC', 'DRIVING_LICENSE', 'POLICE_CLEARANCE_CERTIFICATE'].includes(ptype);

/**
 * Map extraction response fields to sharedMetadata for bulk save.
 * Handles 5 document types: PASSPORT_COPY, VISA_PAGE, EMIRATES_ID, OHC, DRIVING_LICENSE.
 * Returns undefined if no meaningful data extracted.
 */
export const buildSharedMetadataFromExtraction = (backendDocumentType, extraction) => {
  if (!backendDocumentType || !extraction) return undefined;
  const t = String(backendDocumentType || '').toLowerCase();
  const out = {};
  if (t === 'passport_copy') {
    if (extraction.passportNumber || extraction.documentNumber) out.documentNumber = extraction.passportNumber || extraction.documentNumber;
    if (extraction.issueDate || extraction.passportIssueDate) out.issueDate = extraction.issueDate || extraction.passportIssueDate;
    if (extraction.expiryDate || extraction.passportExpiryDate) out.expiryDate = extraction.expiryDate || extraction.passportExpiryDate;
  } else if (t === 'visa_page') {
    if (extraction.visaNumber || extraction.documentNumber) out.documentNumber = extraction.visaNumber || extraction.documentNumber;
    if (extraction.issueDate || extraction.visaIssueDate) out.issueDate = extraction.issueDate || extraction.visaIssueDate;
    if (extraction.expiryDate || extraction.visaExpiryDate) out.expiryDate = extraction.expiryDate || extraction.visaExpiryDate;
  } else if (t === 'emirates_id') {
    if (extraction.emiratesIdNumber || extraction.emiratesId || extraction.documentNumber) out.documentNumber = extraction.emiratesIdNumber || extraction.emiratesId || extraction.documentNumber;
    if (extraction.issueDate || extraction.emiratesIdIssueDate) out.issueDate = extraction.issueDate || extraction.emiratesIdIssueDate;
    if (extraction.expiryDate || extraction.emiratesIdExpiry) out.expiryDate = extraction.expiryDate || extraction.emiratesIdExpiry;
  } else if (t === 'ohc') {
    if (extraction.certificateNumber || extraction.documentNumber) out.documentNumber = extraction.certificateNumber || extraction.documentNumber;
    if (extraction.issueDate) out.issueDate = extraction.issueDate;
    if (extraction.expiryDate) out.expiryDate = extraction.expiryDate;
    if (extraction.ohcResult) out.ohcResult = extraction.ohcResult;
    if (extraction.fullName) out.fullName = extraction.fullName;
    if (extraction.nationality) out.nationality = extraction.nationality;
    if (extraction.companyName) out.companyName = extraction.companyName;
  } else if (t === 'driving_license') {
    if (extraction.licenseNumber || extraction.documentNumber) out.documentNumber = extraction.licenseNumber || extraction.documentNumber;
    if (extraction.issueDate) out.issueDate = extraction.issueDate;
    if (extraction.expiryDate) out.expiryDate = extraction.expiryDate;
    if (extraction.placeOfIssue) out.placeOfIssue = extraction.placeOfIssue;
    if (extraction.fullName) out.fullName = extraction.fullName;
    if (extraction.nationality) out.nationality = extraction.nationality;
    if (extraction.dateOfBirth) out.dateOfBirth = extraction.dateOfBirth;
  } else if (t === 'police_clearance_certificate') {
    if (extraction.referenceNumber || extraction.documentNumber) out.documentNumber = extraction.referenceNumber || extraction.documentNumber;
    if (extraction.issueDate) out.issueDate = extraction.issueDate;
    if (extraction.expiryDate) out.expiryDate = extraction.expiryDate;
    if (extraction.issuingAuthority || extraction.issuing_authority) out.issuingAuthority = extraction.issuingAuthority || extraction.issuing_authority;
  }
  return Object.keys(out).length ? out : undefined;
};

/**
 * Build backend employee update payload for passport fields under uidDetails/personalInfo.
 */
export const buildEmployeeUpdatePayloadFromPassport = (upd) => {
  if (!upd || typeof upd !== 'object') return {};
  const payload = { personalInfo: {}, uidDetails: {} };
  if (upd.passportNumber) {
    payload.personalInfo.passportNumber = upd.passportNumber;
    payload.uidDetails.passportNumber = upd.passportNumber;
    payload.passportNumber = upd.passportNumber;
  }
  if (upd.passportExpiry) {
    payload.personalInfo.passportExpiry = upd.passportExpiry;
    payload.uidDetails.passportExpiry = upd.passportExpiry;
    payload.passportExpiry = upd.passportExpiry;
  }
  if (upd.passportIssue) {
    payload.personalInfo.passportIssue = upd.passportIssue;
    payload.uidDetails.passportIssue = upd.passportIssue;
    payload.passportIssue = upd.passportIssue;
  }
  return payload;
};

/**
 * Build backend employee update payload for OHC fields.
 */
export const buildEmployeeUpdatePayloadFromOhc = (upd) => {
  if (!upd || typeof upd !== 'object') return {};
  const payload = { personalInfo: {} };
  if (upd.ohcIssueDate) {
    payload.personalInfo.ohcIssueDate = upd.ohcIssueDate;
  }
  if (upd.ohcExpiryDate) {
    payload.personalInfo.ohcExpiryDate = upd.ohcExpiryDate;
  }
  return payload;
};

/**
 * Build backend employee update payload for Driving License fields.
 */
export const buildEmployeeUpdatePayloadFromDrivingLicense = (upd) => {
  if (!upd || typeof upd !== 'object') return {};
  const payload = { personalInfo: {} };
  if (upd.drivingLicenseIssueDate) {
    payload.personalInfo.drivingLicenseIssueDate = upd.drivingLicenseIssueDate;
  }
  if (upd.drivingLicenseExpiryDate) {
    payload.personalInfo.drivingLicenseExpiryDate = upd.drivingLicenseExpiryDate;
  }
  if (upd.drivingLicensePlaceOfIssue) {
    payload.personalInfo.drivingLicensePlaceOfIssue = upd.drivingLicensePlaceOfIssue;
  }
  return payload;
};
